import type { ApiClient } from '@carbonwebmail/api-client'
import type { EmailProviderDto, ProviderConfigurationDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Which provider this organisation sends through, and its own credentials.
 *
 * The form starts empty and stays empty after a save, because the server never
 * returns a credential. That is the whole arrangement: values go one way, and a
 * form pre-filled with dots that submit as dots is how half a credential gets
 * saved.
 *
 * The typed values live here and are cleared the moment they are accepted.
 * Holding somebody's API key in component state for longer than the request
 * takes buys nothing and is one more place it can be read from.
 */

export type EmailProviderState = AsyncState<ProviderConfigurationDto>

export interface TestOutcome {
  readonly status: 'sent' | 'refused' | 'unknown' | 'not-configured'
  readonly detail: string | undefined
}

export function useEmailProvider({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.emailProvider(), [client])
  const { state, reload } = useLoaded<ProviderConfigurationDto>(
    load,
    'We could not load your email provider. Please try again in a moment.',
  )

  const [saved, setSaved] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [test, setTest] = useState<TestOutcome | undefined>(undefined)

  const save = useCallback(
    async (provider: EmailProviderDto, settings: Readonly<Record<string, string>>) => {
      setBusy(true)
      setProblem(undefined)
      setSaved(false)
      setTest(undefined)
      try {
        const outcome = await client.saveEmailProvider({ provider, settings })
        if (outcome.status !== 'changed') {
          setProblem(outcome.problem)
          return false
        }
        setSaved(true)
        await reload()
        return true
      } catch {
        setProblem('We could not save that. Please try again in a moment.')
        return false
      } finally {
        setBusy(false)
      }
    },
    [client, reload],
  )

  const clear = useCallback(async () => {
    setBusy(true)
    setProblem(undefined)
    setSaved(false)
    setTest(undefined)
    try {
      await client.clearEmailProvider()
      await reload()
    } catch {
      setProblem('We could not remove those credentials. Please try again in a moment.')
    } finally {
      setBusy(false)
    }
  }, [client, reload])

  const sendTest = useCallback(async () => {
    setBusy(true)
    setProblem(undefined)
    setTest(undefined)
    try {
      const outcome = await client.sendTestEmail()
      setTest({ status: outcome.status, detail: outcome.detail ?? undefined })
    } catch {
      setProblem('We could not send a test just now. Please try again in a moment.')
    } finally {
      setBusy(false)
    }
  }, [client])

  return { state, saved, problem, busy, test, save, clear, sendTest, retry: reload }
}
